/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';

import { makeColumn, row } from '@stories/atoms/Grid';
import HitListView from '@stories/organisms/HitListView';
import HitCompact from '@stories/organisms/HitListView/HitCompact';
import Pagination from '@stories/templates/Search/Pagination/Pagination';
import { media } from '@stories/theming/settings';
import { useProductListContext } from '@utils/productList';

import type { CssProps } from '@stories/@types/custom';

const itemsSeparator = {
  '::after': {
    backgroundColor: 'hsl(0, 0%, 88%)',
    bottom: '-1.5rem',
    content: '""',
    height: '1px',
    left: '50%',
    margin: 'auto',
    maxWidth: '25rem',
    position: 'absolute',
    transform: 'translateX(-50%)',
    width: 'calc(100% - var(--gutter))'
  },
  ':first-child': {
    marginTop: '0.75rem',
    paddingTop: 0
  },
  ':last-child': {
    '::after': {
      content: 'none'
    }
  }
};

const Results: React.FC<CssProps> = ({ cssProps }) => {
  const {
    productsAndAvailableFilters: { hits, total },
    defaultSettings: { productsPerPage }
  } = useProductListContext();
  const [twoColumnsLayout, setTwoColumnsLayout] = useState(false);

  useEffect(() => {
    // @ts-expect-error just for A/B test purpose
    window?.vfABtwoColumns === true && setTwoColumnsLayout(true);
  }, []);

  return (
    <section
      css={{
        overflow: 'hidden',
        ...row,
        [media('tablet')]: { '--gutter': '4rem' },
        ...(cssProps as object),
        ...(twoColumnsLayout && { gap: '5rem 1rem', paddingTop: '1rem' })
      }}
    >
      {hits?.map((productHit) =>
        !twoColumnsLayout ? (
          <HitListView
            cssProps={{
              ...itemsSeparator,
              ...makeColumn(12),
              marginBottom: '1.5rem',
              marginTop: '1.5rem',
              [media('tablet')]: {
                ...makeColumn(6),
                marginBottom: '3rem',
                marginTop: '3rem',
                '::after': { bottom: '-3rem !important' }
              }
            }}
            key={productHit.id}
            {...productHit}
          />
        ) : (
          <HitCompact
            key={productHit.id}
            {...{ ...productHit, id: productHit.id.toString() }}
            border={false}
            cssProps={{ minWidth: '0', flex: '45% 1 1' }}
          />
        )
      )}
      {total > productsPerPage && <Pagination cssProps={{ marginTop: '3rem' }} />}
    </section>
  );
};

export default Results;
